import React, { useRef, useState, useEffect, useCallback } from "react";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

const MIN_SCALE = 0.3;
const MAX_SCALE = 5;
const DEFAULT_SCALE = 1; // "fit width"

/**
 * Visualizador de PDF (páginas já renderizadas em imagens) com pinça, duplo-toque
 * e botões +/- de zoom. Rola dentro do próprio container. Ideal para mobile/iOS.
 */
export default function PinchZoomPdf({ pages }) {
  const [scaleDisplay, setScaleDisplay] = useState(DEFAULT_SCALE);
  const [baseSize, setBaseSize] = useState({ width: 0, height: 0 });
  const viewportRef = useRef(null);
  const sizerRef = useRef(null);
  const contentRef = useRef(null);
  const scaleRef = useRef(DEFAULT_SCALE);
  const gestureRef = useRef(null);

  // Base para caber na largura (recalcula quando o viewport tem largura real)
  const computeBaseSize = useCallback(() => {
    const vp = viewportRef.current;
    if (!pages.length || !vp) return;
    const vpWidth = vp.clientWidth - 16;
    if (vpWidth <= 0) return; // painel ainda oculto (aba não ativa) — ignora
    const maxPageWidth = Math.max(...pages.map((p) => p.width));
    const fit = vpWidth / maxPageWidth;
    const totalHeight = pages.reduce((s, p) => s + p.height * fit, 0) + (pages.length - 1) * 8 + 16;
    setBaseSize((prev) => {
      const w = maxPageWidth * fit + 16;
      if (Math.abs(prev.width - w) < 1) return prev;
      return { width: w, height: totalHeight };
    });
  }, [pages]);

  useEffect(() => {
    computeBaseSize();
  }, [computeBaseSize]);

  // Recalcula quando o container muda de tamanho (ex.: ao trocar para a aba "Novo (PDF)" ou girar a tela)
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => computeBaseSize());
    ro.observe(vp);
    return () => ro.disconnect();
  }, [computeBaseSize]);

  const applyScale = useCallback(
    (newScale, scrollAdjust) => {
      const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
      scaleRef.current = s;
      if (contentRef.current) contentRef.current.style.transform = `scale(${s})`;
      if (sizerRef.current && baseSize.width > 0) {
        sizerRef.current.style.width = `${baseSize.width * s}px`;
        sizerRef.current.style.height = `${baseSize.height * s}px`;
      }
      if (scrollAdjust && viewportRef.current) {
        viewportRef.current.scrollLeft = scrollAdjust.x;
        viewportRef.current.scrollTop = scrollAdjust.y;
      }
      setScaleDisplay(s);
    },
    [baseSize]
  );

  useEffect(() => {
    if (baseSize.width > 0) applyScale(scaleRef.current);
  }, [baseSize, applyScale]);

  const zoomAt = useCallback(
    (newScale, vx, vy) => {
      const vp = viewportRef.current;
      const sizer = sizerRef.current;
      if (!vp) return;
      const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
      const cur = scaleRef.current;
      const sizerRect = sizer?.getBoundingClientRect();
      const vpRect = vp.getBoundingClientRect();
      const curOffsetX = sizerRect ? sizerRect.left - vpRect.left + vp.scrollLeft : 0;
      const curOffsetY = sizerRect ? sizerRect.top - vpRect.top + vp.scrollTop : 0;
      const contentX = (vx + vp.scrollLeft - curOffsetX) / cur;
      const contentY = (vy + vp.scrollTop - curOffsetY) / cur;
      const newSizerW = baseSize.width * clamped;
      const newOffsetX = newSizerW < vp.clientWidth ? (vp.clientWidth - newSizerW) / 2 : 0;
      applyScale(clamped, { x: contentX * clamped + newOffsetX - vx, y: contentY * clamped - vy });
    },
    [applyScale, baseSize]
  );

  // Pinça + duplo-toque
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || !pages.length) return;
    let lastTap = 0;
    let lastPos = { x: 0, y: 0 };

    const onStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const [a, b] = e.touches;
        const rect = vp.getBoundingClientRect();
        const sizerRect = sizerRef.current?.getBoundingClientRect();
        gestureRef.current = {
          startDist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
          startScale: scaleRef.current,
          midX: (a.clientX + b.clientX) / 2 - rect.left,
          midY: (a.clientY + b.clientY) / 2 - rect.top,
          startScrollX: vp.scrollLeft,
          startScrollY: vp.scrollTop,
          sizerOffsetX: sizerRect ? sizerRect.left - rect.left + vp.scrollLeft : 0,
          sizerOffsetY: sizerRect ? sizerRect.top - rect.top + vp.scrollTop : 0,
        };
      }
    };
    const onMove = (e) => {
      if (e.touches.length === 2 && gestureRef.current) {
        e.preventDefault();
        const [a, b] = e.touches;
        const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        const g = gestureRef.current;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, g.startScale * (dist / g.startDist)));
        const contentX = (g.midX + g.startScrollX - g.sizerOffsetX) / g.startScale;
        const contentY = (g.midY + g.startScrollY - g.sizerOffsetY) / g.startScale;
        const newSizerW = baseSize.width * newScale;
        const newOffsetX = newSizerW < vp.clientWidth ? (vp.clientWidth - newSizerW) / 2 : 0;
        applyScale(newScale, {
          x: contentX * newScale + newOffsetX - g.midX,
          y: contentY * newScale - g.midY,
        });
      }
    };
    const onEnd = (e) => {
      if (e.touches.length === 0) {
        gestureRef.current = null;
        if (e.changedTouches.length === 1) {
          const t = e.changedTouches[0];
          const now = Date.now();
          if (now - lastTap < 300 && Math.hypot(t.clientX - lastPos.x, t.clientY - lastPos.y) < 30) {
            const rect = vp.getBoundingClientRect();
            const mx = t.clientX - rect.left;
            const my = t.clientY - rect.top;
            if (scaleRef.current > DEFAULT_SCALE * 1.1) zoomAt(DEFAULT_SCALE, mx, my);
            else zoomAt(2.2, mx, my);
            lastTap = 0;
          } else {
            lastTap = now;
            lastPos = { x: t.clientX, y: t.clientY };
          }
        }
      }
    };
    vp.addEventListener("touchstart", onStart, { passive: false });
    vp.addEventListener("touchmove", onMove, { passive: false });
    vp.addEventListener("touchend", onEnd);
    vp.addEventListener("touchcancel", onEnd);
    return () => {
      vp.removeEventListener("touchstart", onStart);
      vp.removeEventListener("touchmove", onMove);
      vp.removeEventListener("touchend", onEnd);
      vp.removeEventListener("touchcancel", onEnd);
    };
  }, [pages, applyScale, zoomAt, baseSize]);

  const zoomIn = () => {
    const vp = viewportRef.current;
    if (vp) zoomAt(scaleRef.current * 1.3, vp.clientWidth / 2, vp.clientHeight / 2);
  };
  const zoomOut = () => {
    const vp = viewportRef.current;
    if (vp) zoomAt(scaleRef.current / 1.3, vp.clientWidth / 2, vp.clientHeight / 2);
  };
  const reset = () => applyScale(DEFAULT_SCALE, { x: 0, y: 0 });

  return (
    <div className="relative w-full h-full">
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-white/95 border border-gray-300 rounded-full px-2 py-1 shadow">
        <button onClick={zoomOut} data-testid="new-pdf-zoom-out" className="p-1 text-gray-700">
          <ZoomOut className="h-4 w-4" />
        </button>
        <span className="text-xs w-10 text-center tabular-nums text-gray-600">{Math.round(scaleDisplay * 100)}%</span>
        <button onClick={zoomIn} data-testid="new-pdf-zoom-in" className="p-1 text-gray-700">
          <ZoomIn className="h-4 w-4" />
        </button>
        <button onClick={reset} data-testid="new-pdf-zoom-reset" className="p-1 text-gray-700">
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>
      <div
        ref={viewportRef}
        data-testid="new-pdf-zoom-viewport"
        className="w-full h-full overflow-auto bg-gray-300"
        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y", position: "relative" }}
      >
        <div
          ref={sizerRef}
          style={{
            width: baseSize.width ? `${baseSize.width}px` : "100%",
            height: baseSize.height ? `${baseSize.height}px` : "auto",
            position: "relative",
            margin: "0 auto",
          }}
        >
          <div
            ref={contentRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: baseSize.width ? `${baseSize.width}px` : "100%",
              transform: `scale(${DEFAULT_SCALE})`,
              transformOrigin: "0 0",
              willChange: "transform",
              padding: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              boxSizing: "border-box",
            }}
          >
            {pages.map((p, idx) => {
              const maxW = baseSize.width ? baseSize.width - 16 : p.width;
              const fit = Math.min(maxW / p.width, 1.5);
              return (
                <img
                  key={idx}
                  src={p.src}
                  alt={`Página ${idx + 1}`}
                  draggable={false}
                  style={{
                    width: p.width * fit,
                    height: "auto",
                    maxWidth: "100%",
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                    display: "block",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
